import { getLocale } from '@/pages/cogo-marketing/i18n/locale-store'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'
import type { CapabilityGrant } from './manifest-schema'

// ---------------------------------------------------------------------------
// Copy for the "Verified design libraries" panel on /studio/design-system.
//
// WHY THIS EXISTS: measured in a signed-in browser under `cogo-locale=ko`
// (2026-08-18) — the whole admission-evidence panel rendered in English on a
// Korean surface: the heading, the registry note, every admission-stage
// sentence, the six capability-axis names, the generation boundary line, and
// the "Figma Community page" link.
//
// WHERE IT LIVES. `verified-libraries-section.tsx` consumed NEITHER the shared
// `studio-dictionaries.ts` nor any copy module — it held its strings inline.
// So this follows the co-located `COPY` table + `get…Copy(locale)` accessor
// pattern of `appliance-admin-page-copy.ts` / `admin-page-copy.ts`, and is
// deliberately NOT added to the 7,000-line shared dictionary.
//
// The `Record<LocaleCode, …>` below has no spreads: every locale states every
// string, and omitting one is a compile error. That matters because the shared
// dictionary's guarantee is uneven — its locale tables are written
// `{ ...en, … }` and several subtrees are inherited rather than restated
// (`zh.home = { ...en.home }`, `zhTW = { ...zh }`, …), so a key added to `en`
// alone lands in those subtrees as English. Measured 2026-08-18 on the
// `settings` subtree specifically: ko/ja/zh/zh-TW DO restate it in full, so
// there an omission surfaces as a `tsc` error instead — which is why the four
// settings strings this lane fixed could stay in the shared dictionary.
//
// ── WHAT IS TRANSLATED AND WHAT IS NOT ───────────────────────────────────────
//
// NOT translated — registry DATA, passed through verbatim by the component:
//   `sourceId`, `snapshot.status`, the sha256 hex, `receiptId`, the SPDX id
//   and its license status, and `blockedAtGate`. These are the vault's own
//   strings; an operator matches them against the receipt JSON in-repo, so
//   translating one breaks the match rather than improving the reading. The
//   component already documents that snapshot facts "pass through the vault
//   registry's own status strings verbatim"; this module keeps that promise.
//
// TRANSLATED — the six capability axes (`local evaluation`, `ontology
// extraction`, `pack derivation`, `derivative publication`, `raw
// redistribution`, `upstream writeback`). This is the one genuinely arguable
// call, so state the reasoning: they ARE defined domain terms of the
// library-harness rights model, but the greppable, canonical form of each is
// the receipt's own camelCase `grant` key (`ontologyExtraction`, …) — NOT the
// space-separated lowercase phrase, which exists nowhere except in this
// component's own `CAPABILITY_LABELS` table, which invented it purely to be
// read. A term whose only job is to be read must be read in the reader's
// language; nothing an operator matches on is lost, because the thing they
// match on is the camelCase key in their own JSON. Where a Cogo noun already
// has a settled rendering in the shared dictionary this reuses it — `pack`
// follows `settings.plan.packPromotion` (팩 / パック / 包 / 套件), `ontology`
// follows `designSystem.tabs.ontology` (온톨로지 / オントロジー / 本体 / 本體),
// and `publication` follows `settings.plan.publication` (공개).
//
// `Record<keyof CapabilityGrant, string>` on `capabilityLabels` keeps the
// compile-fails-when-an-axis-is-added guarantee the inline table had (same
// reasoning as `DesignAxesCopy.axisLabels`' `Record<AxisName, string>`): a
// seventh grant axis in `manifest-schema.ts` is a type error in five places
// here, not a silently untranslated word on screen.
// ---------------------------------------------------------------------------

export interface VerifiedLibrariesCopy {
  sectionTitle: string
  /** The registry note under the heading — states where rows come from AND that admission is not a generation right. */
  sectionNote: string
  /** Fail-closed card. Receives the reader-opaque schema reason verbatim. */
  unreadable: (reason: string) => string
  /** Readable registry, zero libraries — an ordinary state, not an error. */
  empty: string
  /** Link text to the source's Figma Community listing. `Figma` is a product name and stays. */
  communityLink: string
  /** Prefix of the snapshot line: "Snapshot: <vault status>". */
  snapshotLabel: string
  /** Byte size of the snapshot, already locale-grouped by the caller. */
  bytes: (formattedCount: string) => string
  /** "sha256 <first 12 hex>…" — `sha256` is the algorithm identifier, not copy. */
  sha256: (shortHash: string) => string
  /** " · license <status>: <spdx>" — both values are the receipt's own strings. */
  license: (status: string, spdx: string) => string
  /** Stage `library-reuse-admitted`, immediately followed by the receipt ids. */
  reuseAdmitted: string
  /** Stage `admission-blocked`, with no gate named. */
  admissionBlocked: string
  /** The gate this source stopped at; receives the gate id verbatim. */
  nextRequiredGate: string
  /** Stage `designated`. */
  designated: string
  /** Stage `no-admission-evidence`. */
  noAdmissionEvidence: string
  /** Reuse receipts disagree on scope — the UI must not pick a winner. */
  grantVariesByReceipt: string
  admittedList: (capabilities: string) => string
  notAdmittedList: (capabilities: string) => string
  /** Every axis is denied. */
  admittedNone: string
  /** Separator between the admitted and not-admitted halves of the scope line. */
  scopeSeparator: string
  /** Separator between capability names inside one half. */
  capabilitySeparator: string
  generationEligible: string
  generationNotAdmitted: string
  capabilityLabels: Record<keyof CapabilityGrant, string>
}

const en: VerifiedLibrariesCopy = {
  sectionTitle: 'Verified design libraries',
  sectionNote:
    'Read from the committed admission registry — each row shows exactly what its receipts admit. Admitting a library for evaluation is not a right to generate from it.',
  unreadable: (reason) =>
    `The verified design libraries registry could not be read: ${reason}. No library list is shown — this screen does not guess what was admitted.`,
  empty: 'The registry is readable and lists no libraries yet.',
  communityLink: 'Figma Community page',
  snapshotLabel: 'Snapshot:',
  bytes: (formattedCount) => `${formattedCount} bytes`,
  sha256: (shortHash) => `sha256 ${shortHash}…`,
  license: (status, spdx) => `license ${status}: ${spdx}`,
  reuseAdmitted: 'Library reuse admitted — receipts:',
  admissionBlocked: 'Admission blocked',
  nextRequiredGate: 'next required gate:',
  designated: 'Designated for admission — the admission gates have not run yet.',
  noAdmissionEvidence: 'No admission evidence recorded for this source.',
  grantVariesByReceipt:
    'Capability grants vary by receipt — each receipt carries its own scope.',
  admittedList: (capabilities) => `Admitted: ${capabilities}`,
  notAdmittedList: (capabilities) => `Not admitted: ${capabilities}`,
  admittedNone: 'Admitted: none',
  scopeSeparator: ' · ',
  capabilitySeparator: ', ',
  generationEligible: 'Generation from this library: eligible per the source vault.',
  generationNotAdmitted: 'Generation from this library: not admitted.',
  capabilityLabels: {
    localEvaluation: 'local evaluation',
    ontologyExtraction: 'ontology extraction',
    packDerivation: 'pack derivation',
    derivativePublication: 'derivative publication',
    rawRedistribution: 'raw redistribution',
    upstreamWriteback: 'upstream writeback',
  },
}

const ko: VerifiedLibrariesCopy = {
  sectionTitle: '검증된 디자인 라이브러리',
  sectionNote:
    '커밋된 승인 레지스트리에서 읽습니다 — 각 행은 그 영수증이 승인한 범위만 그대로 보여줍니다. 평가를 위해 라이브러리를 승인한 것이 그 라이브러리로 생성할 권리를 뜻하지는 않습니다.',
  unreadable: (reason) =>
    `검증된 디자인 라이브러리 레지스트리를 읽지 못했습니다: ${reason}. 라이브러리 목록을 보여주지 않습니다 — 이 화면은 무엇이 승인됐는지 추측하지 않습니다.`,
  empty: '레지스트리는 읽었지만, 아직 등록된 라이브러리가 없습니다.',
  communityLink: 'Figma 커뮤니티 페이지',
  snapshotLabel: '스냅샷:',
  bytes: (formattedCount) => `${formattedCount}바이트`,
  sha256: (shortHash) => `sha256 ${shortHash}…`,
  license: (status, spdx) => `라이선스 ${status}: ${spdx}`,
  reuseAdmitted: '라이브러리 재사용 승인됨 — 영수증:',
  admissionBlocked: '승인이 막혔습니다',
  nextRequiredGate: '다음에 통과해야 할 관문:',
  designated: '승인 대상으로 지정됐습니다 — 승인 관문은 아직 실행되지 않았습니다.',
  noAdmissionEvidence: '이 소스에는 기록된 승인 근거가 없습니다.',
  grantVariesByReceipt:
    '권한 범위가 영수증마다 다릅니다 — 각 영수증이 자기 범위를 따로 갖고 있습니다.',
  admittedList: (capabilities) => `승인됨: ${capabilities}`,
  notAdmittedList: (capabilities) => `승인되지 않음: ${capabilities}`,
  admittedNone: '승인됨: 없음',
  scopeSeparator: ' · ',
  capabilitySeparator: ', ',
  generationEligible: '이 라이브러리로 생성: 소스 저장고 기준으로 가능합니다.',
  generationNotAdmitted: '이 라이브러리로 생성: 승인되지 않았습니다.',
  capabilityLabels: {
    localEvaluation: '로컬 평가',
    ontologyExtraction: '온톨로지 추출',
    packDerivation: '팩 파생',
    derivativePublication: '2차 저작물 공개',
    rawRedistribution: '원본 재배포',
    upstreamWriteback: '업스트림 쓰기 반영',
  },
}

const ja: VerifiedLibrariesCopy = {
  sectionTitle: '検証済みデザインライブラリ',
  sectionNote:
    'コミット済みの承認レジストリから読み取ります — 各行はその受領証が承認した範囲だけをそのまま示します。評価のためにライブラリを承認したことは、そこから生成する権利を意味しません。',
  unreadable: (reason) =>
    `検証済みデザインライブラリのレジストリを読み取れませんでした: ${reason}。ライブラリ一覧は表示しません — この画面は何が承認されたかを推測しません。`,
  empty: 'レジストリは読み取れましたが、登録されたライブラリはまだありません。',
  communityLink: 'Figma コミュニティページ',
  snapshotLabel: 'スナップショット:',
  bytes: (formattedCount) => `${formattedCount} バイト`,
  sha256: (shortHash) => `sha256 ${shortHash}…`,
  license: (status, spdx) => `ライセンス ${status}: ${spdx}`,
  reuseAdmitted: 'ライブラリ再利用が承認されました — 受領証:',
  admissionBlocked: '承認が止まっています',
  nextRequiredGate: '次に通過が必要なゲート:',
  designated: '承認対象に指定されました — 承認ゲートはまだ実行されていません。',
  noAdmissionEvidence: 'このソースには記録された承認の根拠がありません。',
  grantVariesByReceipt:
    '権限の範囲は受領証ごとに異なります — 各受領証がそれぞれの範囲を持ちます。',
  admittedList: (capabilities) => `承認済み: ${capabilities}`,
  notAdmittedList: (capabilities) => `未承認: ${capabilities}`,
  admittedNone: '承認済み: なし',
  scopeSeparator: ' · ',
  capabilitySeparator: '、',
  generationEligible: 'このライブラリからの生成: ソース保管庫の記録では可能です。',
  generationNotAdmitted: 'このライブラリからの生成: 承認されていません。',
  capabilityLabels: {
    localEvaluation: 'ローカル評価',
    ontologyExtraction: 'オントロジー抽出',
    packDerivation: 'パック派生',
    derivativePublication: '二次的著作物の公開',
    rawRedistribution: '原本の再配布',
    upstreamWriteback: 'アップストリームへの書き戻し',
  },
}

const zh: VerifiedLibrariesCopy = {
  sectionTitle: '已验证的设计库',
  sectionNote:
    '读取自已提交的准入登记表 — 每一行只呈现其凭据实际准入的范围。为评估而准入一个库，并不等于拥有据此生成的权利。',
  unreadable: (reason) =>
    `无法读取已验证设计库的登记表：${reason}。不展示任何库列表 — 本页面不会猜测什么被准入了。`,
  empty: '登记表可以读取，但尚未列出任何库。',
  communityLink: 'Figma 社区页面',
  snapshotLabel: '快照：',
  bytes: (formattedCount) => `${formattedCount} 字节`,
  sha256: (shortHash) => `sha256 ${shortHash}…`,
  license: (status, spdx) => `许可证 ${status}：${spdx}`,
  reuseAdmitted: '库复用已准入 — 凭据：',
  admissionBlocked: '准入被拦下',
  nextRequiredGate: '下一个必须通过的关卡：',
  designated: '已指定为准入对象 — 准入关卡尚未运行。',
  noAdmissionEvidence: '此来源没有记录任何准入证据。',
  grantVariesByReceipt: '各凭据授予的权限范围不同 — 每份凭据各自带着自己的范围。',
  admittedList: (capabilities) => `已准入：${capabilities}`,
  notAdmittedList: (capabilities) => `未准入：${capabilities}`,
  admittedNone: '已准入：无',
  scopeSeparator: ' · ',
  capabilitySeparator: '、',
  generationEligible: '基于此库生成：按来源库房记录可行。',
  generationNotAdmitted: '基于此库生成：未准入。',
  capabilityLabels: {
    localEvaluation: '本地评估',
    ontologyExtraction: '本体抽取',
    packDerivation: '包派生',
    derivativePublication: '衍生作品发布',
    rawRedistribution: '原始文件再分发',
    upstreamWriteback: '回写上游',
  },
}

const zhTW: VerifiedLibrariesCopy = {
  sectionTitle: '已驗證的設計庫',
  sectionNote:
    '讀取自已提交的准入登記表 — 每一列只呈現其憑據實際准入的範圍。為評估而准入一個庫，並不等於擁有據此產生的權利。',
  unreadable: (reason) =>
    `無法讀取已驗證設計庫的登記表：${reason}。不顯示任何庫清單 — 本頁面不會猜測什麼被准入了。`,
  empty: '登記表可以讀取，但尚未列出任何庫。',
  communityLink: 'Figma 社群頁面',
  snapshotLabel: '快照：',
  bytes: (formattedCount) => `${formattedCount} 位元組`,
  sha256: (shortHash) => `sha256 ${shortHash}…`,
  license: (status, spdx) => `授權 ${status}：${spdx}`,
  reuseAdmitted: '庫重複使用已准入 — 憑據：',
  admissionBlocked: '准入被擋下',
  nextRequiredGate: '下一個必須通過的關卡：',
  designated: '已指定為准入對象 — 准入關卡尚未執行。',
  noAdmissionEvidence: '此來源沒有記錄任何准入證據。',
  grantVariesByReceipt: '各憑據授予的權限範圍不同 — 每份憑據各自帶著自己的範圍。',
  admittedList: (capabilities) => `已准入：${capabilities}`,
  notAdmittedList: (capabilities) => `未准入：${capabilities}`,
  admittedNone: '已准入：無',
  scopeSeparator: ' · ',
  capabilitySeparator: '、',
  generationEligible: '以此庫產生：依來源庫房記錄為可行。',
  generationNotAdmitted: '以此庫產生：未准入。',
  capabilityLabels: {
    localEvaluation: '本機評估',
    ontologyExtraction: '本體抽取',
    packDerivation: '套件衍生',
    derivativePublication: '衍生作品發布',
    rawRedistribution: '原始檔案再散布',
    upstreamWriteback: '回寫上游',
  },
}

const COPY: Record<LocaleCode, VerifiedLibrariesCopy> = {
  en,
  ko,
  ja,
  zh,
  'zh-TW': zhTW,
}

export function getVerifiedLibrariesCopy(
  locale: LocaleCode = getLocale(),
): VerifiedLibrariesCopy {
  return COPY[locale] ?? COPY.en
}

/** Exported for the residue gate, which asserts every locale renders its own table. */
export const VERIFIED_LIBRARIES_COPY = COPY
